import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Parameter definitions
const parameterDefinitions = [
  { key: "serialNumber", label: "Rejected Module SN" },
  { key: "defectName", label: "Defect Name" },
];

const RecordRejectedModuleTable = ({ data = [], startDate, endDate, shifts = [], subForm }) => {
  // Get all dates strictly in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, data]);

  // Build lookup map for modules by date and shift
  const dataMap = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;
      const key = `${date}_${shift}`;
      map[key] = item.form_data?.rejectedModules || [];
    });
    return map;
  }, [data]);

  // Determine number of module columns needed per date-shift (minimum 1)
  const maxModulesPerShift = useMemo(() => {
    const maxCounts = {};
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const moduleCount = (dataMap[key] || []).length;
        maxCounts[key] = Math.max(moduleCount, 1);
      });
    });
    return maxCounts;
  }, [dates, effectiveShifts, dataMap]);

  // Generate hierarchical structure: Date -> Shifts -> Modules
  const headerStructure = useMemo(() => {
    return dates.map((date) => {
      let dateColSpan = 0;
      const shiftNodes = effectiveShifts.map((shift) => {
        const key = `${date}_${shift}`;
        const count = maxModulesPerShift[key] || 1;
        dateColSpan += count;

        const modules = Array.from({ length: count }, (_, i) => ({
          id: `${key}_module_${i + 1}`,
          label: `Module ${i + 1}`,
          moduleIndex: i,
          date,
          shift,
        }));

        return {
          shift,
          colSpan: count,
          modules,
        };
      });

      return {
        date,
        colSpan: dateColSpan,
        shifts: shiftNodes,
      };
    });
  }, [dates, effectiveShifts, maxModulesPerShift]);

  // Flattened leaf columns for the table body
  const flatColumns = useMemo(() => {
    return headerStructure.flatMap((d) => d.shifts.flatMap((s) => s.modules));
  }, [headerStructure]);

  // Column width calculations
  const columnWidths = useMemo(() => {
    return {
      parameter: 220,
      value: 140,
    };
  }, []);

  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Static parameter rows
  const tableRows = useMemo(() => {
    return parameterDefinitions.map((param) => ({
      key: param.key,
      parameter: param.label,
    }));
  }, []);

  // Fast map lookup: `${date}_${shift}_${moduleIndex}_${paramKey}`
  const tableDataMap = useMemo(() => {
    const map = {};
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const modules = dataMap[key] || [];
        const count = maxModulesPerShift[key] || 1;

        for (let i = 0; i < count; i++) {
          const mod = modules[i];
          parameterDefinitions.forEach((param) => {
            const val = mod ? mod[param.key] : "N/A";
            map[`${key}_${i}_${param.key}`] = val && val !== "" ? String(val) : "N/A";
          });
        }
      });
    });
    return map;
  }, [dates, effectiveShifts, dataMap, maxModulesPerShift]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Parameter"];
    const row2 = [""];
    const row3 = [""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 2, c: 0 } });

    let colIdx = 1;

    headerStructure.forEach((dateNode) => {
      row1.push(dateNode.date);
      for (let i = 1; i < dateNode.colSpan; i++) row1.push("");
      if (dateNode.colSpan > 1) {
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + dateNode.colSpan - 1 } });
      }

      dateNode.shifts.forEach((shiftNode) => {
        row2.push(`Shift ${shiftNode.shift}`);
        for (let i = 1; i < shiftNode.colSpan; i++) row2.push("");
        if (shiftNode.colSpan > 1) {
          merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + shiftNode.colSpan - 1 } });
        }

        shiftNode.modules.forEach((mod) => {
          row3.push(mod.label);
          colIdx += 1;
        });
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      flatColumns.forEach((col) => {
        const val = tableDataMap[`${col.date}_${col.shift}_${col.moduleIndex}_${row.key}`];
        r.push(val !== undefined ? val : "N/A");
      });
      return r;
    });

    const sheetData = [row1, row2, row3, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 25 },
      ...Array(flatColumns.length).fill({ wch: 22 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Rejected Modules");

    const fileName = generateExcelFileName("Record_Rejected_Modules", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Record of Rejected Modules During Inspection
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Parameter + Dates (merged across shifts and their modules) */}
            <TableRow>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {headerStructure.map((dateNode) => (
                <CustomTableCell
                  key={dateNode.date}
                  colSpan={dateNode.colSpan}
                  columnWidth={columnWidths.value * dateNode.colSpan}
                >
                  {dateNode.date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shifts (sub-columns under Date) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.map((shiftNode) => (
                  <CustomTableCell
                    key={`${dateNode.date}_${shiftNode.shift}`}
                    colSpan={shiftNode.colSpan}
                    columnWidth={columnWidths.value * shiftNode.colSpan}
                  >
                    Shift {shiftNode.shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>

            {/* Row 3: Modules (sub-columns under Shift) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.flatMap((shiftNode) =>
                  shiftNode.modules.map((mod) => (
                    <CustomTableCell
                      key={mod.id}
                      columnWidth={columnWidths.value}
                    >
                      {mod.label}
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={1}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>

                  {flatColumns.map((col) => {
                    const val = tableDataMap[`${col.date}_${col.shift}_${col.moduleIndex}_${row.key}`];
                    return (
                      <BodyTableCell key={`${col.id}_${row.key}`} columnWidth={columnWidths.value}>
                        {val !== undefined ? val : "N/A"}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default RecordRejectedModuleTable;